// User-level (not per-league) draft milestone counters, for feedback prompts.
//
// Keys follow the app-wide `auctionArchitect_` convention and are deliberately
// NOT scoped to a league or a saved build — this counts how many mock drafts
// this person has finished, across every league they've set up.
//
//   auctionArchitect_completedDraftCount   — integer, drafts finished
//   auctionArchitect_feedbackAskDismissed  — bool, user closed the ask
//   auctionArchitect_feedbackAskSubmitted  — bool, user sent feedback
//
// The count is bumped inside captureMockDraftCompleted (mockAnalytics.js) so it
// can never drift from the mock_draft_completed event — one call site, both
// effects. Unlike the PostHog event, this bump is NOT gated by host/opt-out;
// it's local-only state driving local UI, so it also counts on localhost.
//
// Every accessor swallows storage errors (private mode, quota, disabled
// storage) — a milestone counter must never break the draft loop.

const COUNT_KEY = "auctionArchitect_completedDraftCount";
const TRENDS_ASK_KEY = "auctionArchitect_trendsAsk_v1";
const DISMISSED_KEY = "auctionArchitect_feedbackAskDismissed";
const SUBMITTED_KEY = "auctionArchitect_feedbackAskSubmitted";

export function getCompletedDraftCount() {
  try {
    const n = parseInt(localStorage.getItem(COUNT_KEY), 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

// Returns the new count (or the unchanged count if storage is unavailable).
export function bumpCompletedDraftCount() {
  const next = getCompletedDraftCount() + 1;
  try {
    localStorage.setItem(COUNT_KEY, String(next));
    return next;
  } catch {
    return next - 1;
  }
}

export function getFeedbackAskDismissed() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}

export function setFeedbackAskDismissed(value) {
  try {
    localStorage.setItem(DISMISSED_KEY, value ? "true" : "false");
  } catch {
    /* ignore */
  }
}

export function getFeedbackAskSubmitted() {
  try {
    return localStorage.getItem(SUBMITTED_KEY) === "true";
  } catch {
    return false;
  }
}

export function setFeedbackAskSubmitted(value) {
  try {
    localStorage.setItem(SUBMITTED_KEY, value ? "true" : "false");
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// PRE-DRAFT TRENDS ASK
// ---------------------------------------------------------------------------
// One JSON blob under auctionArchitect_trendsAsk_v1, unlike the three flat
// feedback keys above — this ask has three related numbers and splitting them
// across keys is how they get read inconsistently later.
//
// THE RULES FOR THE FULL ASK, in one place so they cannot drift from the code:
//   submitted            → never show again, full stop.
//   dismissCount >= 3    → never show again.
//   dismissed once/twice → suppress the next 2 completions, then show once more.
//
// AFTER SUBMITTING, the full ask is done forever but the card is not: a compact
// confirmed state takes its place, and when no draft date is on file that state
// asks for one. THE LEAK THIS CLOSES: a signup with no date lands in the
// endpoint's trends_by_date:unscheduled bucket and can never be served, because
// the full ask never returns and there is no other path to supply a date.
//   submittedDraftDate set   → one-line reassurance, no prompt.
//   no date, not dismissed   → reassurance plus a date field.
//   no date, dismissed       → nothing. Nobody is nagged twice for a date they
//                              may genuinely not have yet.
//
// dateAskDismissed is a SEPARATE flag from dismissCount on purpose. Folding it
// in would let a date dismissal count toward the full ask's window and, worse,
// let the full ask resurrect itself for someone who already signed up.
//
// submittedEmail is stored because the date-only follow-up has to POST a valid
// address: the canonical record is keyed on sha256(email) and overwrites, so
// supplying the date later just fills it in, and an invalid email is the
// endpoint's only 400.
//
// 🔴 dismissedAtCount IS NOT OPTIONAL, even though the field list for this key
// was specified as { submitted, dismissCount }. "Suppress the next 2
// completions" is a statement about elapsed drafts, and dismissCount alone
// cannot express it — without a mark for WHEN the dismissal happened there is
// nothing to count forward from, and the ask would come back on the very next
// completion.
//
// Storage being unavailable degrades toward SHOWING the ask (all reads fall
// back to zeros). That is the right direction for a card the user can dismiss
// in one click, and matches how the feedback ask behaves.

export const TRENDS_ASK_MAX_DISMISSALS = 3;
export const TRENDS_ASK_SUPPRESS_COMPLETIONS = 2;

const EMPTY_TRENDS_ASK = {
  submitted: false,
  dismissCount: 0,
  dismissedAtCount: 0,
  submittedEmail: "",
  submittedDraftDate: null,
  dateAskDismissed: false,
};

// The endpoint's own bucket format. Validated on read rather than trusted: a
// hand-edited or half-written value must read as "no date on file" and route to
// the prompt, never render as a garbage date in the reassurance line.
const DRAFT_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const cleanDate = (v) => (typeof v === "string" && DRAFT_DATE_RE.test(v) ? v : null);

// Every field is re-derived rather than trusted: this blob is user-writable
// (it is localStorage) and a hand-edited `submitted: "no"` must not read as a
// truthy string.
export function readTrendsAskState() {
  try {
    const raw = JSON.parse(localStorage.getItem(TRENDS_ASK_KEY) || "{}");
    const int = (v) => (Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
    return {
      submitted: raw?.submitted === true,
      dismissCount: int(raw?.dismissCount),
      dismissedAtCount: int(raw?.dismissedAtCount),
      submittedEmail: typeof raw?.submittedEmail === "string" ? raw.submittedEmail : "",
      submittedDraftDate: cleanDate(raw?.submittedDraftDate),
      dateAskDismissed: raw?.dateAskDismissed === true,
    };
  } catch {
    return { ...EMPTY_TRENDS_ASK };
  }
}

function writeTrendsAskState(next) {
  try {
    localStorage.setItem(TRENDS_ASK_KEY, JSON.stringify(next));
  } catch {
    /* ignore: a frequency cap must never break the results screen */
  }
}

// Called only after the signup POST has come back 200 — a submitted flag set on
// a request the server never saw would silently retire the ask for someone who
// is not actually signed up.
// `draftDate` is the YYYY-MM-DD the ENDPOINT returned, not a locally derived
// one: the server buckets on US Eastern, so its answer is the only value that
// agrees with which day the send will actually go out.
export function setTrendsAskSubmitted({ email, draftDate } = {}) {
  writeTrendsAskState({
    ...readTrendsAskState(),
    submitted: true,
    submittedEmail: typeof email === "string" ? email : "",
    submittedDraftDate: cleanDate(draftDate),
  });
}

// The date-only follow-up landed. Everything else on the blob is preserved, so
// this cannot disturb the submitted flag or the dismissal history.
export function setTrendsAskDraftDate(draftDate) {
  writeTrendsAskState({ ...readTrendsAskState(), submittedDraftDate: cleanDate(draftDate) });
}

// Suppresses the date prompt for good. Deliberately NOT bumpTrendsAskDismissed:
// this must never feed the full ask's window.
export function setTrendsDateAskDismissed() {
  writeTrendsAskState({ ...readTrendsAskState(), dateAskDismissed: true });
}

// `completedCount` is stamped in so the suppression window can be counted
// forward from here. Returns the new dismiss count.
export function bumpTrendsAskDismissed(completedCount) {
  const prev = readTrendsAskState();
  const next = {
    ...prev,
    dismissCount: prev.dismissCount + 1,
    dismissedAtCount: Number.isFinite(completedCount) && completedCount > 0
      ? Math.floor(completedCount)
      : prev.dismissedAtCount,
  };
  writeTrendsAskState(next);
  return next.dismissCount;
}

// Pure, and takes both inputs — so the rules can be tested without touching
// storage, and so a caller cannot accidentally test a different clock.
export function trendsAskEligible(state, completedCount) {
  if (!state || state.submitted) return false;
  if (state.dismissCount >= TRENDS_ASK_MAX_DISMISSALS) return false;
  if (state.dismissCount === 0) return true;
  // Dismissed at N: N+1 and N+2 are suppressed, N+3 shows.
  return completedCount >= state.dismissedAtCount + TRENDS_ASK_SUPPRESS_COMPLETIONS + 1;
}

// Which compact state replaces the full ask once someone has signed up.
//   "dated"      → one-line reassurance naming the send day.
//   "needs-date" → reassurance plus a date field, the unscheduled-bucket fix.
//   "none"       → render nothing.
// Pure and total, so the component never has to reason about the combinations.
export function trendsConfirmedVariant(state) {
  if (!state || !state.submitted) return "none";
  if (state.submittedDraftDate) return "dated";
  return state.dateAskDismissed ? "none" : "needs-date";
}

export function shouldShowTrendsAsk() {
  return trendsAskEligible(readTrendsAskState(), getCompletedDraftCount());
}
